import { useState } from "react";
import { useSelector } from "react-redux";
import { HotelsShopSelectors } from "@b2bportal/core-hotels";
import { removeHTMLTags } from "@b2bportal/core-utilities";
import type { LodgingTravelWalletOffer } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import { OfferBanner } from "../OfferBanner";
import { OfferDialog } from "../OfferDialog";
import {
  HtsMediaClaimDialog,
  lodgingOfferDialogContent,
} from "@/modules/htsMedia/HtsMediaClaimDialog";
import {
  ClaimStatus,
  IsOfferClaimed,
  OfferRawEventName,
  OfferScreen,
  OfferType,
  useEmitRbcRawEvent,
  useEmitRbcRawEventOnce,
} from "@/tracking/offers";
import { TenantIconNameEnum } from "@/types/tenantIcons";

/** ads-orchestrator prefixes every HTS Media (DMO) offer name with this. */
export const HTS_MEDIA_OFFER_NAME_PREFIX = "Ads-Campaign-";

/** Scranton offer name; the BFF puts it in the tracking properties, and leaves it as the title when it has no content. */
export const lodgingOfferName = (offer: LodgingTravelWalletOffer): string => {
  const name: unknown = offer.trackingPropertiesV2?.properties?.offer_name;
  return typeof name === "string" ? name : offer.title;
};

export const isHtsMediaOffer = (offer: LodgingTravelWalletOffer) =>
  lodgingOfferName(offer).startsWith(HTS_MEDIA_OFFER_NAME_PREFIX);

/** An HTS Media offer the BFF could not describe still carries its campaign name as the title; never show that. */
export const isDisplayableLodgingOffer = (offer: LodgingTravelWalletOffer) =>
  offer.title.trim() !== "" &&
  !offer.title.startsWith(HTS_MEDIA_OFFER_NAME_PREFIX);

export interface WalletOfferBannerProps {
  /** Offer to show; when omitted, the lodging's best offer from the shop store. */
  offer?: LodgingTravelWalletOffer;
  /** Surface the banner renders on; segments the offer events. */
  screen?: OfferScreen;
  className?: string;
}

/**
 * Banner for a wallet offer the lodging response says applies here
 * (Figma 40548-40219): "• Applied at checkout ›", opening the offer dialog.
 * The offer is already reflected in the prices; nothing is fetched.
 */
export const WalletOfferBanner = ({
  offer: givenOffer,
  screen = OfferScreen.hotel_list,
  className,
}: WalletOfferBannerProps = {}) => {
  const { t } = useI18nContext();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const shopBestOffer = useSelector(HotelsShopSelectors.getBestOffer);
  const candidate = givenOffer ?? shopBestOffer;
  const offer =
    candidate != null && isDisplayableLodgingOffer(candidate)
      ? candidate
      : undefined;

  // Only HTS Media offers have an offer_type today; other wallet offers stay untracked.
  const tracked = offer != null && isHtsMediaOffer(offer);
  const trackingProperties = {
    offer_type: OfferType.hts_media,
    offer_id: offer?.id ?? "",
    offer_name: offer != null ? lodgingOfferName(offer) : "",
    screen,
    is_claimable: true,
  };
  const emitTappedBanner = useEmitRbcRawEvent(
    OfferRawEventName.TappedOfferBanner
  );
  useEmitRbcRawEventOnce(
    {
      name: OfferRawEventName.ViewedOfferBanner,
      properties: {
        ...trackingProperties,
        is_offer_eligible: true,
        is_offer_claimed: IsOfferClaimed.true,
      },
    },
    tracked
  );

  if (!offer) return null;

  const title = removeHTMLTags(offer.title);
  const openDialog = () => {
    if (tracked)
      emitTappedBanner({
        properties: {
          ...trackingProperties,
          claim_status: ClaimStatus.already_claimed,
          is_offer_claimed: IsOfferClaimed.true,
        },
      });
    setIsDialogOpen(true);
  };
  const closeDialog = () => setIsDialogOpen(false);

  return (
    <>
      <OfferBanner
        className={className}
        icon={<Icon iconName={TenantIconNameEnum.offersPercent} aria-hidden />}
        title={title}
        status={{ label: t("walletOfferBanner.appliedAtCheckout") }}
        onClick={openDialog}
        ariaLabel={t("walletOfferBanner.appliedAriaLabel", { title })}
      />
      {/* HTS Media offers get the same details dialog as a claimable ad. */}
      {tracked ? (
        <HtsMediaClaimDialog
          open={isDialogOpen}
          content={lodgingOfferDialogContent(offer, lodgingOfferName(offer))}
          ctaLabel={t("htsMedia.dialog.ctaDetails")}
          onCtaClick={closeDialog}
          onClose={closeDialog}
          screen={screen}
        />
      ) : (
        <OfferDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          title={title}
          description={removeHTMLTags(offer.descriptions.join(" "))}
          ctaLabel={t("walletOfferBanner.dialogCta")}
        />
      )}
    </>
  );
};
